<template>
  <div>
    <LoadingContainer
      :is-loaded="isLoaded"
      :total="presetsLength"
      kind="Presets"
    >
      <TransitionList class="presets-list">
        <li v-for="p in presets" :key="p.id" class="preset-card">
          <span class="preset-card__description">
            {{ p.description }}
          </span>

          <span class="preset-card__subject" :style="`--color:${getSubjectColor(p.subject)}`">
            {{ getSubjectName(p.subject) }}
          </span>

          <span class="preset-card__duration">
            {{ p.duration }}min
          </span>

          <button class="preset-card__delete button" @click="deletePreset(p.id)">
            <span class="u-screen-reader-only">Delete Preset</span>
            <DeleteIcon />
          </button>

          <button class="preset-card__edit button" @click="editPreset(p.id)">
            <span class="u-screen-reader-only">Edit Preset</span>
            <EditIcon />
          </button>

          <ConfirmDelete
            :is-visible="isDeleting(p.id)"
            class="preset-card__confirm"
            :title="p.description"
            @cancel="cancelDelete"
            @confirm="confirmDeletePreset(p.id)"
          />

          <PresetForm
            class="preset-card__form"
            v-if="isEditing(p.id)"
            @submit="saveEditedPreset"
            :preset="{...p}"
            :title="`Edit Preset: ${p.description}`"
            @cancel="cancelEditPreset"
          ></PresetForm>
        </li>
      </TransitionList>
    </LoadingContainer>

    <PresetForm
      v-if="showAdd"
      @submit="saveNewPreset"
      :preset="newPreset"
      @cancel="clearPreset"
    ></PresetForm>

    <SettingsAdd v-else @add="addPreset">
      Add Preset
    </SettingsAdd>
  </div>
</template>

<script>
import store from '@/store'
import { mapActions, mapGetters } from 'vuex'

import DeleteIcon from 'bootstrap-icons/icons/trash.svg'
import EditIcon from 'bootstrap-icons/icons/pencil-square.svg'
import ConfirmDelete from '@/components/ConfirmDelete'
import PresetForm from '@/views/Settings/Forms/PresetForm'

export default {
  name: 'SettingsPresets',

  components: {
    DeleteIcon,
    EditIcon,
    ConfirmDelete,
    PresetForm
  },

  data () {
    return {
      editingPreset: undefined,
      deletingPreset: undefined,
      newPreset: {
        subject: []
      }
    }
  },

  computed: {
    ...mapGetters({
      isLoaded: 'presets/isLoaded',
      presetsLength: 'presets/presetsLength',
      presets: 'presets/sortedPresets',
      subjects: 'subjects/sortedSubjects'
    }),

    showAdd () {
      return this.$route.query.add
    }
  },

  methods: {
    ...mapActions([
      'presets/addPreset',
      'presets/deletePreset',
      'presets/editPreset'
    ]),

    getSubject (id) {
      const subjectId = Array.isArray(id)
        ? id[0]
        : id

      return this.subjects.find(s => s.id === subjectId)
    },

    getSubjectColor (subjectId) {
      const subject = this.getSubject(subjectId)
      return (subject && 'color' in subject)
        ? subject.color
        : '#eee'
    },

    getSubjectName (subjectId) {
      const subject = this.getSubject(subjectId)
      return (subject && 'name' in subject)
        ? subject.name
        : 'Unnamed Subject'
    },

    editPreset (id) {
      this.editingPreset = id
    },

    isEditing (id) {
      return id === this.editingPreset
    },

    saveEditedPreset (preset) {
      this['presets/editPreset'](preset)
      this.editingPreset = undefined
    },

    cancelEditPreset () {
      this.editingPreset = undefined
    },

    deletePreset (id) {
      this.deletingPreset = id
    },

    isDeleting (id) {
      return id === this.deletingPreset
    },

    cancelDelete () {
      this.deletingPreset = undefined
    },

    confirmDeletePreset (id) {
      this['presets/deletePreset'](id)
      this.deletingPreset = undefined
    },

    addPreset () {
      this.$router.replace({
        name: 'settings-presets',
        query: {
          add: true
        }
      })
    },

    clearPreset () {
      this.$router.replace({
        name: 'settings-presets'
      })
    },

    saveNewPreset () {
      this['presets/addPreset'](this.newPreset)

      this.clearPreset()
    }
  },

  mounted () {
    store.dispatch('subjects/getSubjects')
    store.dispatch('presets/getPresets')
  }
}
</script>

<style lang="scss">
.preset-card {
  margin-bottom: 2rem;
  display: grid;
  grid-template:
    "desc    desc    desc     delete edit" auto
    "subject .       duration delete edit" auto
    "form    form    form     form  form" auto /
     auto    1rem    1fr      auto   auto;
  align-items: center;
}

.preset-card__description {
  grid-area: desc;
  font-size: 1.4rem;
  font-weight: 700;
}

.preset-card__subject {
  padding: .25rem 1rem;
  grid-area: subject;
  justify-self: start;
  font-size: .8rem;
  text-transform: uppercase;
  letter-spacing: (1rem / 32);
  background-color: var(--color, #FFF);
  border-radius: 1rem;
}

.preset-card__duration {
  grid-area: duration;
  font-size: .8rem;
}

.preset-card__delete {
  --button-fg: #B71C1C;
  --button-fg-active: #D32F2F;
  --button-width: 3rem;

  grid-area: delete;
}

.preset-card__edit {
  --button-fg: #006064;
  --button-fg-active: #0097A7;
  --button-width: 3rem;

  grid-area: edit;
}

.preset-card__form {
  margin-top: 1rem;
  padding: 1rem;
  grid-area: form;
  border-radius: 2.25rem;
  background-color: #fff;
}

.preset-card__confirm {
  grid-area: form;
}
</style>
